import React from 'react';
import { Box, keyframes, Stack, type SxProps, Typography } from '@mui/material';
import { vars } from '../../../Theme';
import { useSelector } from 'react-redux';
import { eplassSliceViewMode } from '../../../Redux/EplassSlice';
import { PdfViewerViewModeFlag } from '../../../pdf/Enums';
import { generateUniqueKey } from '../../../utils';

/**
 * Loading component animation frames.
 */
const movement = keyframes`
    0% {
        transform: translate3d(0, -25px, 0);
        z-index: 0;
    }
    50% {
        transform: translate3d(0, 25px, 0);
        z-index: 10;
    }
    100% {
        transform: translate3d(0, -25px, 0);
        z-index: -5;
    }
`;

/** Styles for making an element flexbox-compatible. */
const flexBase: SxProps = {
    display: 'flex',
    alignItems: 'center',
};

/** Styles for making an element take up all available size from its parent. */
const fullSize: SxProps = {
    height: '100%',
    width: '100%',
};

/**
 * Gets the styles for a given dot in the loader.
 * @param delay - Animation start delay.
 * @param i - Dot index.
 */
const dotSx = (delay: number, i: number): SxProps => ({
    transition: 'all 0.5s ease',
    transform: 'translate3d(0, -25px, 0) scale(1)',
    animation: `${movement} 2s infinite ease`,
    animationDelay: `-${delay.toFixed(1)}s`,
    bgcolor: !(i % 2) ? vars.brandBlue : vars.brandGreen,
    top: 'calc(50% - 6px)',
    borderRadius: '100%',
    position: 'absolute',
    height: 12,
    width: 12,
    left: (Math.floor(i / 2) + 1) * 25,
});

/** Styles for the Typography component that displays a message. */
const loadingMessageSx: SxProps = {
    wordWrap: 'break-word',
    textAlign: 'center',
    fontWeight: 'bold',
};

const loaderSx = {
    ...flexBase,
    ...fullSize,
    backgroundColor: '#FFFFFF',
    position: 'absolute',
    justifyContent: 'center',
    zIndex: '10',
} as SxProps;

const loaderHiddenSx = {
    display: 'none',
} as SxProps;

/**
 * Loading screen component that looks like a DNA helix.
 */
export default (props: { message?: string }) => {
    const viewMode = useSelector(eplassSliceViewMode);

    /** Create 26 dots for the loading helix. */
    const dots = new Array(26).fill(null).map((_, i) => {
        const delay = i % 2 === 0 ? 0.1 * (i + 1.1) : Math.floor(i / 2) * 0.2 + 1;

        return <Box key={generateUniqueKey('loading', i)} sx={dotSx(delay, i)} />;
    });

    return (
        <Box sx={viewMode === PdfViewerViewModeFlag.Loading ? loaderSx : loaderHiddenSx}>
            <Stack alignItems="center">
                <Box sx={{ height: 100, width: 360, position: 'relative' }}>{dots}</Box>

                {props.message && (
                    <Typography sx={loadingMessageSx} variant="h5">
                        {props.message}
                    </Typography>
                )}
            </Stack>
        </Box>
    );
};
